import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { BrandingConfig } from './branding-config.model';

// Branded portals run on "<subdomain>.portal[-<env>].vaylens.com", e.g. "1komma5.portal-int.vaylens.com" -> "1komma5".
// The main portal (portal[-<env>].vaylens.com) and localhost have no subdomain.
const BRANDED_PORTAL_HOST = /^([a-z0-9-]+)\.portal(?:-[a-z]+)?\.vaylens\.com$/i;

export function extractBrandedSubdomain(hostname: string): string | null {
  return BRANDED_PORTAL_HOST.exec(hostname ?? '')?.[1]?.toLowerCase() ?? null;
}

@Injectable({
  providedIn: 'root',
})
export class BrandingConfigService {
  private readonly http = inject(HttpClient);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly brandingConfigSubject = new BehaviorSubject<BrandingConfig | null>(null);

  /** Branding config of the current subdomain; null until loaded or if there is none. */
  get brandingConfig$(): Observable<BrandingConfig | null> {
    return this.brandingConfigSubject.asObservable();
  }

  getCurrentSubdomain(): string | null {
    return extractBrandedSubdomain(this.document.location?.hostname);
  }

  /**
   * Loads the branding config of the current subdomain. A subdomain without branding config is not registered
   * as redirect URI in Keycloak, so the user is redirected to the main portal instead.
   *
   * @returns false if the user is redirected to the main portal, true otherwise
   */
  async loadBrandingConfig(): Promise<boolean> {
    const subdomain = this.getCurrentSubdomain();
    if (!subdomain) {
      return true;
    }
    try {
      const config = await firstValueFrom(
        this.http.get<BrandingConfig>(environment.brandingConfigUrl, { params: { subdomain } })
      );
      this.brandingConfigSubject.next(config);
    } catch (error) {
      if (
        error instanceof HttpErrorResponse &&
        (error.status === HttpStatusCode.NotFound || error.status === HttpStatusCode.Forbidden)
      ) {
        this.redirectToMainPortal();
        return false;
      }
    }
    return true;
  }

  private redirectToMainPortal(): void {
    const { pathname, search } = this.document.location;
    this.document.location.replace(
      `https://${environment.brandedPortalDomain}${pathname}${search}`
    );
  }
}
